/**
 * Mehr-Standort Phase 3 (· 265) — Hauptsitz-Org-Cockpit.
 *
 * Nur für Hauptsitze sichtbar. Bietet:
 *  - Scope-Umschalter: „Alle Standorte" / einzelne Filiale (lesend aggregiert).
 *  - KPI-Kacheln für den gewählten Scope (Summe bzw. einzelner Standort).
 *  - Belegnummern-Übersicht je Standort (zuletzt vergebene RG/WO/INT/BST inkl.
 *    Standort-Code-Suffix) — macht die org-weite Nummernvergabe transparent.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Building2, Layers, FileText, Loader2, RefreshCw, TrendingUp, Truck, Wrench, BookmarkPlus, Check, FilePlus2, Coins, ChevronDown } from "lucide-react";
import api, { formatPrice, formatDate } from "../../lib/api";
import { toast } from "sonner";
import OrgInboxPanel from "./OrgInboxPanel";
import OrgTasksPanel from "./OrgTasksPanel";
import PotentialCard from "./PotentialCard";
import BranchLoginPanel from "./BranchLoginPanel";
import { usePersistentToggle } from "../../lib/usePersistentToggle";

// Jun-2026 (User-Wunsch) — KPI-Kacheln sind anklickbar und führen auf die
// passende (ggf. gefilterte) Seite bzw. springen zum jeweiligen Panel.
const KPI_TILES = [
    { key: "active_listings", label: "Aktive Inserate", tone: "text-[#111827]", to: "/dashboard?tab=listings" },
    { key: "workshop_open", label: "Offene Werkstatt", tone: "text-[#0EA5E9]", to: "/dealer/workshop-orders?status=open" },
    { key: "invoices_open", label: "Offene Rechnungen", tone: "text-amber-600", to: "/dealer/invoices" },
    { key: "invoices_overdue", label: "Überfällig", tone: "text-[#DC2626]", to: "/dealer/invoices?overdue=1" },
    { key: "tasks_open", label: "Offene Aufgaben", tone: "text-violet-600", scrollTo: "org-tasks-panel" },
];

const DOC_LABELS = [
    { key: "RG", label: "Rechnung" },
    { key: "RG_DISPO", label: "Dispo-RG" },
    { key: "WO", label: "Werkstatt" },
    { key: "INT", label: "Intern" },
    { key: "BST", label: "Bestellung" },
];

/** Mini-SVG-Sparkline (keine externe Lib) — zeigt einen Werteverlauf. */
function Sparkline({ values, width = 96, height = 26, stroke = "#0EA5E9" }) {
    const max = Math.max(1, ...values);
    const n = values.length;
    if (n === 0) return null;
    const stepX = n > 1 ? width / (n - 1) : 0;
    const pts = values.map((v, i) => {
        const x = i * stepX;
        const y = height - 2 - (v / max) * (height - 4);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
    });
    const last = values[n - 1];
    const lastY = height - 2 - (last / max) * (height - 4);
    return (
        <svg width={width} height={height} className="overflow-visible" aria-hidden="true">
            <polyline points={pts.join(" ")} fill="none" stroke={stroke} strokeWidth="1.5"
                strokeLinejoin="round" strokeLinecap="round" />
            <circle cx={(n - 1) * stepX} cy={lastY} r="2" fill={stroke} />
        </svg>
    );
}

export default function OrgCockpit({ user }) {
    const navigate = useNavigate();
    const [scope, setScope] = useState("all");
    const [overview, setOverview] = useState(null);
    const [docNumbers, setDocNumbers] = useState(null);
    const [trend, setTrend] = useState(null);
    const [transfers, setTransfers] = useState(null);
    const [topPositions, setTopPositions] = useState(null);
    const [platformFees, setPlatformFees] = useState(null);
    const [loading, setLoading] = useState(true);
    const [savingCat, setSavingCat] = useState(null);
    const [savedCats, setSavedCats] = useState({});
    const [docNumbersOpen, setDocNumbersOpen] = usePersistentToggle("org-cockpit-docnumbers-open", false);
    const [trendOpen, setTrendOpen] = usePersistentToggle("org-cockpit-trend-open", false);
    const [feesOpen, setFeesOpen] = usePersistentToggle("org-cockpit-fees-open", false);
    const [topPosOpen, setTopPosOpen] = usePersistentToggle("org-cockpit-toppos-open", false);
    // Jun-2026 (User-Wunsch) — Detail-Bereich unter den KPI-Kacheln einklappbar;
    // klappt automatisch auf, sobald ein Standort (oder „Alle") gewählt wird.
    const [detailsOpen, setDetailsOpen] = usePersistentToggle("org-cockpit-details-open", false);
    // Aug-2026 (Bugfix): Deep-Link ?focus=org-tasks muss die Details öffnen,
    // sonst ist das Aufgaben-Panel gar nicht gemountet.
    const [cockpitSearchParams] = useSearchParams();
    useEffect(() => {
        if (cockpitSearchParams.get("focus") === "org-tasks") setDetailsOpen(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cockpitSearchParams]);

    const isHauptsitz = user?.branch_role === "hauptsitz";

    // Kachel-Klick: navigiert auf die passende Seite oder springt zum Panel.
    // Bei gewähltem Einzel-Standort (scope) werden die Ziele standortgefiltert.
    const openTile = useCallback((tile) => {
        if (tile.scrollTo) {
            const el = document.querySelector(`[data-testid="${tile.scrollTo}"]`);
            if (el) {
                const collapsed = el.querySelector('[aria-expanded="false"]');
                if (collapsed) { try { collapsed.click(); } catch { /* ignore */ } }
                el.scrollIntoView({ behavior: "smooth", block: "start" });
            }
            return;
        }
        if (!tile.to) return;
        let to = tile.to;
        const branch = scope && scope !== "all" ? scope : null;
        if (branch) {
            if (tile.key === "workshop_open") {
                sessionStorage.setItem("tw_ws_branch", branch);
            } else if (to.startsWith("/dealer/invoices")) {
                to += (to.includes("?") ? "&" : "?") + `branch=${branch}`;
            }
        }
        navigate(to);
    }, [navigate, scope]);

    const loadOverview = useCallback((sc) => {
        setLoading(true);
        api.get("/org/overview", { params: { scope: sc } })
            .then((r) => setOverview(r.data))
            .catch(() => setOverview(null))
            .finally(() => setLoading(false));
    }, []);

    const loadDocNumbers = useCallback(() => {
        api.get("/org/document-numbers")
            .then((r) => setDocNumbers(r.data))
            .catch(() => setDocNumbers(null));
    }, []);

    const loadTrend = useCallback(() => {
        api.get("/org/revenue-trend", { params: { months: 6 } })
            .then((r) => setTrend(r.data))
            .catch(() => setTrend(null));
    }, []);

    const loadTransfers = useCallback(() => {
        api.get("/org/transfers", { params: { limit: 12 } })
            .then((r) => setTransfers(r.data))
            .catch(() => setTransfers(null));
    }, []);

    const loadTopPositions = useCallback(() => {
        api.get("/org/top-positions-by-category", { params: { limit: 5 } })
            .then((r) => setTopPositions(r.data))
            .catch(() => setTopPositions(null));
    }, []);

    const loadPlatformFees = useCallback(() => {
        api.get("/org/platform-fees", { params: { months: 6 } })
            .then((r) => setPlatformFees(r.data))
            .catch(() => setPlatformFees(null));
    }, []);

    useEffect(() => {
        if (!isHauptsitz) return;
        loadOverview(scope);
    }, [scope, isHauptsitz, loadOverview]);

    useEffect(() => {
        if (!isHauptsitz) return;
        loadDocNumbers();
        loadTrend();
        loadTransfers();
        loadTopPositions();
        loadPlatformFees();
    }, [isHauptsitz, loadDocNumbers, loadTrend, loadTransfers, loadTopPositions]);

    const refreshAll = () => { loadOverview(scope); loadDocNumbers(); loadTrend(); loadTransfers(); loadPlatformFees(); };

    // Baut die Auftragspositionen aus den Top-Positionen eines Fahrzeugtyps.
    const buildPositions = (cat) => (cat.positions || [])
        .filter((p) => p.label)
        .map((p) => ({
            type: "labour",
            description: p.label,
            quantity: 1,
            unit: "AW",
            unit_price_net: p.count > 0 ? Math.round((p.revenue / p.count) * 100) / 100 : 0,
            vat_rate: 19,
            billing_category: "customer",
        }));

    // „Als Vorlage speichern" — wandelt die Top-Positionen eines Fahrzeugtyps in
    // eine wiederverwendbare Werkstatt-Auftragsvorlage (Preset) um. Der Netto-VK
    // je Position wird als Ø aus Umsatz ÷ Menge abgeleitet.
    const savePreset = async (cat) => {
        const positions = buildPositions(cat);
        if (positions.length === 0) {
            toast.error("Keine Positionen zum Speichern vorhanden.");
            return;
        }
        setSavingCat(cat.category);
        try {
            await api.post("/dealer/work-order-templates", {
                name: `Top-Positionen ${cat.label}`,
                positions,
                vehicle_categories: [cat.category],
            });
            setSavedCats((s) => ({ ...s, [cat.category]: true }));
            toast.success(`Vorlage „Top-Positionen ${cat.label}" gespeichert (${positions.length} Positionen).`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Vorlage konnte nicht gespeichert werden.");
        } finally {
            setSavingCat(null);
        }
    };

    // „Im neuen Auftrag verwenden" — öffnet die Auftragsanlage mit vorbefüllten
    // Top-Positionen dieses Fahrzeugtyps (Kunde wird beim Anlegen gewählt).
    const openInNewOrder = (cat) => {
        const positions = buildPositions(cat);
        if (positions.length === 0) {
            toast.error("Keine Positionen vorhanden.");
            return;
        }
        navigate("/dealer/workshop-orders", {
            state: { newOrderPositions: positions, templateName: `Top-Positionen ${cat.label}` },
        });
    };

    const branches = docNumbers?.locations || overview?.locations || [];

    const maxTrendTotal = useMemo(
        () => Math.max(1, ...((trend?.locations || []).map((l) => l.total || 0))),
        [trend],
    );

    const kpis = useMemo(() => {
        if (!overview) return null;
        if (scope === "all") return overview.totals;
        return overview.locations[0]?.kpis || null;
    }, [overview, scope]);

    if (!isHauptsitz) return null;

    return (
        <div className="mb-8 border border-[#111827]/15 rounded-sm bg-white shadow-sm overflow-hidden" data-testid="org-cockpit">
            {/* Kopf */}
            <div className="bg-[#111827] text-white px-4 py-3 flex items-center gap-2 flex-wrap">
                <Building2 className="w-5 h-5 text-[#FFCC00]" />
                <h3 className="font-display font-black text-lg">Org-Cockpit · Hauptsitz</h3>
                <span className="text-[11px] text-gray-300 font-bold uppercase tracking-wider">
                    {branches.length} Standort{branches.length === 1 ? "" : "e"}
                </span>
                <button
                    type="button"
                    onClick={refreshAll}
                    className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-gray-300 hover:text-white"
                    data-testid="org-cockpit-refresh"
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Aktualisieren
                </button>
            </div>

            <div className="p-4">
                {/* Scope-Umschalter */}
                <div className="flex items-center gap-1.5 mb-4 flex-wrap" data-testid="org-scope-switch">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1 mr-1">
                        <Layers className="w-3.5 h-3.5" /> Ansicht
                    </span>
                    <button
                        type="button"
                        onClick={() => { setScope("all"); setDetailsOpen(true); }}
                        className={`h-8 px-3 rounded-sm text-xs font-bold uppercase tracking-wider border transition-colors ${scope === "all" ? "bg-[#FFCC00] text-[#111827] border-[#FFCC00]" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}
                        data-testid="org-scope-all"
                    >
                        Alle Standorte
                    </button>
                    {(overview?.locations || docNumbers?.locations || []).map((b) => (
                        <button
                            key={b.dealer_id}
                            type="button"
                            onClick={() => { setScope(b.dealer_id); setDetailsOpen(true); }}
                            className={`h-8 px-3 rounded-sm text-xs font-bold uppercase tracking-wider border transition-colors ${scope === b.dealer_id ? "bg-[#FFCC00] text-[#111827] border-[#FFCC00]" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}
                            data-testid={`org-scope-${b.dealer_id}`}
                            title={b.street || b.company_name}
                        >
                            {b.site_code !== "—" ? b.site_code : b.company_name}
                            {b.is_self && " ★"}
                        </button>
                    ))}
                </div>

                {/* KPI-Kacheln */}
                {loading && !kpis ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center" data-testid="org-cockpit-loading">
                        <Loader2 className="w-4 h-4 animate-spin" /> Lade Kennzahlen…
                    </div>
                ) : kpis ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-5" data-testid="org-kpi-grid">
                        {KPI_TILES.map((tile) => (
                            <button key={tile.key} type="button" onClick={() => openTile(tile)}
                                className="text-left border border-gray-200 rounded-sm p-3.5 bg-gray-50 transition-colors hover:border-[#111827]/40 hover:bg-white cursor-pointer" data-testid={`org-kpi-${tile.key}`}>
                                <div className={`font-mono-num font-black text-[1.7rem] leading-none ${tile.tone}`}>
                                    {kpis[tile.key] ?? 0}
                                </div>
                                <div className="text-[11px] uppercase tracking-wide text-gray-600 font-bold mt-1.5">{tile.label}</div>
                            </button>
                        ))}
                        <button type="button" onClick={() => navigate("/dealer/invoices")}
                            className="text-left border border-[#111827] rounded-sm p-3.5 bg-[#111827] text-white transition-colors hover:bg-black cursor-pointer" data-testid="org-kpi-revenue_mtd">
                            <div className="font-mono-num font-black text-[1.7rem] leading-none text-[#FFCC00]">
                                {formatPrice(kpis.revenue_mtd ?? 0)}
                            </div>
                            <div className="text-[11px] uppercase tracking-wide text-white/75 font-bold mt-1.5">Rechnungsvol. MTD</div>
                        </button>
                    </div>
                ) : null}

                {/* Detail-Bereich ein-/ausklappen (Standort-Klick öffnet automatisch) */}
                <button type="button" onClick={() => setDetailsOpen((v) => !v)}
                    className="w-full flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-gray-600 hover:text-[#111827] border-y border-gray-100 py-2 mb-4 transition-colors"
                    data-testid="org-details-toggle" aria-expanded={detailsOpen}>
                    <ChevronDown className={`w-4 h-4 transition-transform ${detailsOpen ? "" : "-rotate-90"}`} />
                    {detailsOpen ? "Details ausblenden" : "Details einblenden"} · Potenziale, Smart-Inbox, Aufgaben & Standort-Auswertungen
                </button>

                {detailsOpen && (<>
                {/* Filialen-Logins — Einladungs-/Login-Status + „Alle erinnern" (User-Wunsch). */}
                <BranchLoginPanel />

                {/* Potenziale — offener, noch nicht abgerechneter Umsatz (User-Wunsch:
                    im Org-Cockpit oberhalb der Smart-Inbox). */}
                <div className="mb-4" data-testid="org-potential-section">
                    <PotentialCard />
                </div>

                {/* Mehr-Standort Phase 4 — Org-weite Smart-Inbox + Aufgaben */}
                <OrgInboxPanel scope={scope} />
                <OrgTasksPanel scope={scope} />

                {/* Standort-Vergleich — Rechnungsvolumen letzte 6 Monate (Sparkline + Balken) */}
                {(trend?.locations || []).length > 0 && (
                    <div className="mb-6" data-testid="org-revenue-trend">
                        <button type="button" onClick={() => setTrendOpen((v) => !v)}
                            className="w-full flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-gray-500 hover:text-gray-700 mb-2"
                            data-testid="org-revenue-trend-toggle" aria-expanded={trendOpen}>
                            <TrendingUp className="w-3.5 h-3.5" /> Standort-Vergleich · Rechnungsvolumen letzte 6 Monate
                            <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform ${trendOpen ? "rotate-180" : ""}`} />
                        </button>
                        {trendOpen && (
                        <div className="space-y-1.5">
                            {trend.locations.map((loc) => {
                                const pct = Math.round(((loc.total || 0) / maxTrendTotal) * 100);
                                const isTop = loc.total === maxTrendTotal && maxTrendTotal > 0;
                                return (
                                    <div key={loc.dealer_id} onClick={() => setScope(loc.dealer_id)} title="Auf diesen Standort filtern"
                                        className={`flex items-center gap-3 cursor-pointer rounded-sm px-1 -mx-1 transition-colors ${scope === loc.dealer_id ? "bg-[#FFCC00]/10" : "hover:bg-gray-50"}`}
                                        data-testid={`org-trend-row-${loc.dealer_id}`}>
                                        <div className="w-28 shrink-0 text-xs truncate">
                                            <span className="font-bold text-[#111827]">{loc.site_code}</span>
                                            {loc.is_self && <span className="text-[#FFCC00] ml-1" title="Hauptsitz">★</span>}
                                            <span className="text-gray-600 ml-1 hidden sm:inline">{loc.street}</span>
                                        </div>
                                        <Sparkline values={loc.series || []} stroke={isTop ? "#16A34A" : "#0EA5E9"} />
                                        <div className="flex-1 h-3 bg-gray-100 rounded-sm overflow-hidden min-w-[60px]">
                                            <div
                                                className={`h-full rounded-sm transition-all ${isTop ? "bg-green-600" : "bg-[#0EA5E9]"}`}
                                                style={{ width: `${pct}%` }}
                                            />
                                        </div>
                                        <div className="w-24 shrink-0 text-right font-mono-num text-xs font-bold text-gray-800" data-testid={`org-trend-total-${loc.dealer_id}`}>
                                            {formatPrice(loc.total || 0)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        )}
                    </div>
                )}

                {/* Plattform-Gebühren (Stripe Connect) je Standort — Umsatzkanal der Plattform.
                    Zeigt 0, solange Connect nicht live ist (Hinweis darunter). */}
                {platformFees && (
                    <div className="mb-6" data-testid="org-platform-fees">
                        <div className="flex items-center justify-between mb-2 gap-2">
                            <button type="button" onClick={() => setFeesOpen((v) => !v)}
                                className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-gray-500 hover:text-gray-700"
                                data-testid="org-platform-fees-toggle" aria-expanded={feesOpen}>
                                <Coins className="w-3.5 h-3.5 text-[#FFCC00]" /> Plattform-Gebühren (Stripe Connect) · letzte 6 Monate
                                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${feesOpen ? "rotate-180" : ""}`} />
                            </button>
                            <div className="text-right">
                                <div className="font-mono-num text-base font-black text-emerald-700" data-testid="org-platform-fees-total">{formatPrice(platformFees.total_fees || 0)}</div>
                                <div className="text-[10px] text-gray-600">aus {formatPrice(platformFees.total_volume || 0)} Zahlungsvolumen</div>
                            </div>
                        </div>
                        {feesOpen && (<>
                        <div className="space-y-1.5">
                            {(platformFees.locations || []).map((loc) => {
                                const maxFee = Math.max(...(platformFees.locations || []).map((l) => l.total_fees || 0), 1);
                                const pct = Math.round(((loc.total_fees || 0) / maxFee) * 100);
                                return (
                                    <div key={loc.dealer_id} onClick={() => setScope(loc.dealer_id)} title="Auf diesen Standort filtern"
                                        className={`flex items-center gap-3 cursor-pointer rounded-sm px-1 -mx-1 transition-colors ${scope === loc.dealer_id ? "bg-[#FFCC00]/10" : "hover:bg-gray-50"}`}
                                        data-testid={`org-fee-row-${loc.dealer_id}`}>
                                        <div className="w-28 shrink-0 text-xs truncate">
                                            <span className="font-bold text-[#111827]">{loc.site_code}</span>
                                            {loc.is_self && <span className="text-[#FFCC00] ml-1" title="Hauptsitz">★</span>}
                                            <span className="text-gray-600 ml-1 hidden sm:inline">{loc.street}</span>
                                        </div>
                                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-gray-100 text-gray-600 shrink-0" data-testid={`org-fee-pct-${loc.dealer_id}`}>{Number(loc.fee_percent || 0).toLocaleString("de-DE")} %</span>
                                        <div className="flex-1 h-3 bg-gray-100 rounded-sm overflow-hidden min-w-[60px]">
                                            <div className="h-full rounded-sm bg-[#FFCC00]" style={{ width: `${pct}%` }} />
                                        </div>
                                        <div className="w-24 shrink-0 text-right font-mono-num text-xs font-bold text-gray-800" data-testid={`org-fee-total-${loc.dealer_id}`}>
                                            {formatPrice(loc.total_fees || 0)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {(platformFees.total_fees || 0) === 0 && (
                            <div className="mt-2 text-[11px] text-gray-600" data-testid="org-platform-fees-empty">
                                Noch keine Gebühren vereinnahmt. Sobald Stripe Connect mit echten Keys aktiv ist und je Händler ein Gebühren-Prozentsatz gesetzt wurde, werden hier die vereinnahmten Plattform-Gebühren je Standort sichtbar.
                            </div>
                        )}
                        </>)}
                    </div>
                )}

                {/* Top-Auftragspositionen je Fahrzeugtyp (· 267) */}
                {(topPositions?.categories || []).length > 0 && (
                    <div className="mb-6" data-testid="org-top-positions-by-category">
                        <button type="button" onClick={() => setTopPosOpen((v) => !v)}
                            className="w-full flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-gray-500 hover:text-gray-700 mb-2"
                            data-testid="org-top-positions-toggle" aria-expanded={topPosOpen}>
                            <Wrench className="w-3.5 h-3.5" /> Top-Auftragspositionen je Fahrzeugtyp
                            <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform ${topPosOpen ? "rotate-180" : ""}`} />
                        </button>
                        {topPosOpen && (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                            {topPositions.categories.map((cat) => {
                                const useRevenue = (cat.total_revenue || 0) > 0;
                                const maxVal = Math.max(...cat.positions.map((p) => (useRevenue ? p.revenue : p.count)), 1);
                                return (
                                    <div key={cat.category} className="border border-gray-200 rounded-sm p-3 bg-gray-50/50" data-testid={`org-topcat-${cat.category}`}>
                                        <div className="flex items-baseline justify-between mb-2">
                                            <span className="font-display font-black text-sm">{cat.label}</span>
                                            <span className="font-mono-num text-xs font-bold text-gray-700">{formatPrice(cat.total_revenue || 0)}</span>
                                        </div>
                                        <button
                                            onClick={() => savePreset(cat)}
                                            disabled={savingCat === cat.category || savedCats[cat.category]}
                                            className={`w-full mb-2 inline-flex items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border transition-colors ${savedCats[cat.category] ? "bg-emerald-50 text-emerald-700 border-emerald-200 cursor-default" : "bg-white text-gray-600 border-gray-300 hover:border-[#111827] hover:text-[#111827]"} disabled:opacity-60`}
                                            data-testid={`org-topcat-save-preset-${cat.category}`}
                                            title="Top-Positionen als Werkstatt-Auftragsvorlage speichern"
                                        >
                                            {savedCats[cat.category] ? (
                                                <><Check className="w-3 h-3" /> Vorlage gespeichert</>
                                            ) : savingCat === cat.category ? (
                                                <><Loader2 className="w-3 h-3 animate-spin" /> Speichert …</>
                                            ) : (
                                                <><BookmarkPlus className="w-3 h-3" /> Als Vorlage speichern</>
                                            )}
                                        </button>
                                        <button
                                            onClick={() => openInNewOrder(cat)}
                                            className="w-full mb-2 inline-flex items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border border-[#FFCC00] bg-[#FFF9E6] text-[#7a6500] hover:bg-[#FFCC00] hover:text-[#111827] transition-colors"
                                            data-testid={`org-topcat-use-order-${cat.category}`}
                                            title="Neuen Werkstatt-Auftrag mit diesen Top-Positionen anlegen"
                                        >
                                            <FilePlus2 className="w-3 h-3" /> Im neuen Auftrag verwenden
                                        </button>
                                        <div className="space-y-1.5">
                                            {cat.positions.map((p, i) => {
                                                const val = useRevenue ? p.revenue : p.count;
                                                const pct = Math.round((val / maxVal) * 100);
                                                return (
                                                    <div key={i} onClick={() => openInNewOrder(cat)} title="Neuen Auftrag mit diesen Top-Positionen anlegen"
                                                        className="cursor-pointer rounded-sm px-1 -mx-1 hover:bg-white transition-colors"
                                                        data-testid={`org-topcat-${cat.category}-pos-${i}`}>
                                                        <div className="flex items-center justify-between gap-2 text-[11px]">
                                                            <span className="truncate text-gray-700" title={p.label}>{p.label}</span>
                                                            <span className="shrink-0 font-mono-num text-gray-500">{useRevenue ? formatPrice(p.revenue) : `${p.count}×`}</span>
                                                        </div>
                                                        <div className="h-1.5 bg-gray-100 rounded-sm overflow-hidden mt-0.5">
                                                            <div className="h-full bg-[#0EA5E9] rounded-sm" style={{ width: `${pct}%` }} />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        )}
                    </div>
                )}


                {/* Belegnummern-Übersicht je Standort */}
                {branches.length > 0 && (
                    <div data-testid="org-doc-numbers">
                        <button type="button" onClick={() => setDocNumbersOpen((v) => !v)}
                            className="w-full flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-gray-500 hover:text-gray-700 mb-2"
                            data-testid="org-doc-numbers-toggle" aria-expanded={docNumbersOpen}>
                            <FileText className="w-3.5 h-3.5" /> Zuletzt vergebene Belegnummern je Standort
                            <ChevronDown className={`w-3.5 h-3.5 ml-auto transition-transform ${docNumbersOpen ? "rotate-180" : ""}`} />
                        </button>
                        {docNumbersOpen && (
                        <div className="overflow-x-auto" data-testid="org-doc-numbers-table">
                            <table className="w-full text-xs border-collapse">
                                <thead>
                                    <tr className="text-left text-gray-700 uppercase tracking-wider text-[10px] border-b-2 border-gray-300">
                                        <th className="py-2 pr-3 font-black">Standort</th>
                                        {DOC_LABELS.map((d) => (
                                            <th key={d.key} className="py-2 px-2 font-black">{d.label}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {(docNumbers?.locations || []).map((loc) => (
                                        <tr key={loc.dealer_id} onClick={() => setScope(loc.dealer_id)} title="Auf diesen Standort filtern"
                                            className={`border-b border-gray-200 cursor-pointer transition-colors ${scope === loc.dealer_id ? "bg-[#FFCC00]/10" : "hover:bg-gray-50"}`}
                                            data-testid={`org-doc-row-${loc.dealer_id}`}>
                                            <td className="py-2 pr-3">
                                                <span className="font-black text-[#111827]">{loc.site_code}</span>
                                                <span className="text-gray-700 ml-1">{loc.street}</span>
                                                {loc.is_self && <span className="text-[#FFCC00] ml-1" title="Hauptsitz">★</span>}
                                            </td>
                                            {DOC_LABELS.map((d) => {
                                                const v = loc.numbers?.[d.key];
                                                return (
                                                    <td key={d.key} className="py-2 px-2 font-mono-num">
                                                        {v ? <span className="text-gray-900 font-bold">{v.number}</span> : <span className="text-gray-400">—</span>}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        )}
                    </div>
                )}

                {/* Org-weite Umlagerungs-Historie (Cross-Branch-Transporte) */}
                {(transfers?.transfers || []).length > 0 && (
                    <div className="mt-6" data-testid="org-transfer-history">
                        <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 inline-flex items-center gap-1 mb-2">
                            <Truck className="w-3.5 h-3.5" /> Umlagerungs-Historie
                            {transfers.open_count > 0 && (
                                <span className="ml-1 bg-amber-100 text-amber-800 px-1.5 rounded-sm" data-testid="org-transfer-open-count">{transfers.open_count} offen</span>
                            )}
                        </div>
                        <div className="space-y-1">
                            {transfers.transfers.map((tr) => {
                                const badge = {
                                    requested: { t: "angefragt", c: "bg-amber-100 text-amber-800" },
                                    in_transit: { t: "unterwegs", c: "bg-blue-100 text-blue-800" },
                                    arrived: { t: "angekommen", c: "bg-emerald-100 text-emerald-800" },
                                    stored: { t: "eingelagert", c: "bg-teal-100 text-teal-800" },
                                    cancelled: { t: "storniert", c: "bg-gray-100 text-gray-700" },
                                }[tr.status] || { t: tr.status, c: "bg-gray-100 text-gray-600" };
                                return (
                                    <div key={tr.id} className="flex items-center gap-2 text-[11px] border-b border-gray-100 pb-1" data-testid={`org-transfer-row-${tr.id}`}>
                                        <span className={`px-1.5 rounded-sm font-bold uppercase tracking-wider text-[9px] shrink-0 ${badge.c}`}>{badge.t}</span>
                                        <span className="font-mono-num font-bold shrink-0">{tr.quantity}×</span>
                                        <span className="font-bold text-[#111827] truncate">{tr.article_name}</span>
                                        <span className="text-gray-500 shrink-0">{tr.source_site_code} → {tr.target_site_code}</span>
                                        <span className="ml-auto text-gray-600 shrink-0 hidden sm:inline">{formatDate(tr.created_at)}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                </>)}
            </div>
        </div>
    );
}
